import { useState } from "react";
import { toast } from "sonner";
import { Phone, Mail, Send, CheckCircle2, Loader2 } from "lucide-react";

const WEB3FORMS_URL = "https://api.web3forms.com/submit";
const ACCESS_KEY = process.env.REACT_APP_WEB3FORMS_ACCESS_KEY;

const services = [
  "AI / ML Solutions",
  "Mobile App Development",
  "Custom SaaS Development",
  "Web Development",
  "UI / UX Design",
  "Cloud & DevOps",
  "Not sure yet",
];

const empty = { name: "", email: "", phone: "", company: "", service: "", message: "", botcheck: "" };

export const Contact = () => {
  const [form, setForm] = useState(empty);
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  const onChange = (e) => setForm({ ...form, [e.target.name]: e.target.value });

  const onSubmit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.message) {
      toast.error("Please fill in name, email and message.");
      return;
    }
    if (!ACCESS_KEY) {
      toast.error("Contact form is not configured. Please email us directly.");
      return;
    }
    // Honeypot: if filled, silently drop
    if (form.botcheck) {
      setSubmitted(true);
      setForm(empty);
      return;
    }

    setSubmitting(true);
    try {
      const payload = {
        access_key: ACCESS_KEY,
        subject: `New contact form submission — ${form.name}`,
        from_name: "Paragon Website",
        name: form.name,
        email: form.email,
        phone: form.phone || "-",
        company: form.company || "-",
        service: form.service || "-",
        message: form.message,
        replyto: form.email,
      };

      const res = await fetch(WEB3FORMS_URL, {
        method: "POST",
        headers: { "Content-Type": "application/json", Accept: "application/json" },
        body: JSON.stringify(payload),
      });
      const data = await res.json();

      if (res.ok && data.success) {
        toast.success("Message sent! We'll get back within 24 hours.");
        setSubmitted(true);
        setForm(empty);
      } else {
        throw new Error(data.message || "Submission failed");
      }
    } catch (err) {
      toast.error(err?.message || "Something went wrong. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  const inputCls =
    "w-full bg-white border-[3px] border-gray-900 rounded-xl px-5 py-3.5 text-base font-medium nb-shadow-sm focus:outline-none focus:shadow-[6px_6px_0px_0px_rgba(17,24,39,1)] transition-all placeholder:text-gray-400";

  return (
    <section
      id="contact"
      data-testid="contact-section"
      className="py-24 md:py-32 bg-[#FAFAFA] border-b-4 border-gray-900"
    >
      <div className="max-w-7xl mx-auto px-6 md:px-10 grid lg:grid-cols-2 gap-12 items-start">
        <div>
          <span className="inline-block bg-[#FF477E] text-white border-[3px] border-gray-900 rounded-full px-4 py-1 text-xs font-bold uppercase tracking-[0.2em] nb-shadow-sm">
            Let's Build
          </span>
          <h2 className="font-display mt-5 text-4xl sm:text-5xl md:text-6xl leading-tight">
            Got an idea?<br />
            <span className="bg-[#FBD46D] px-2 border-[3px] border-gray-900 inline-block -rotate-1 nb-shadow-sm">Let's ship it.</span>
          </h2>
          <p className="mt-6 text-lg md:text-xl font-medium text-gray-700 max-w-lg">
            Tell us what you're building. We'll reply within 24 hours with honest feedback, a rough scope and next steps.
          </p>

          <div className="mt-10 space-y-4">
            <a
              data-testid="contact-phone"
              href="tel:+918130775107"
              className="flex items-center gap-4 bg-white border-[3px] border-gray-900 rounded-xl p-5 nb-shadow-sm nb-hover"
            >
              <div className="w-12 h-12 rounded-lg bg-[#6BCB77] border-[3px] border-gray-900 flex items-center justify-center">
                <Phone size={20} />
              </div>
              <div>
                <div className="text-xs font-bold uppercase tracking-widest text-gray-600">Call</div>
                <div className="font-display text-xl">+91 8130775107</div>
              </div>
            </a>
            <a
              data-testid="contact-email"
              href="mailto:info@paragoninfotech.com"
              className="flex items-center gap-4 bg-white border-[3px] border-gray-900 rounded-xl p-5 nb-shadow-sm nb-hover"
            >
              <div className="w-12 h-12 rounded-lg bg-[#4D96FF] border-[3px] border-gray-900 flex items-center justify-center">
                <Mail size={20} className="text-white" />
              </div>
              <div>
                <div className="text-xs font-bold uppercase tracking-widest text-gray-600">Email</div>
                <div className="font-display text-xl break-all">info@paragoninfotech.com</div>
              </div>
            </a>
          </div>
        </div>

        <form
          data-testid="contact-form"
          onSubmit={onSubmit}
          className="bg-white border-[4px] border-gray-900 rounded-2xl nb-shadow p-6 md:p-8 space-y-5"
        >
          {submitted ? (
            <div data-testid="contact-success" className="flex flex-col items-center text-center py-10">
              <div className="w-16 h-16 rounded-full bg-[#6BCB77] border-[4px] border-gray-900 flex items-center justify-center mb-5">
                <CheckCircle2 size={30} />
              </div>
              <h3 className="font-display text-3xl mb-2">Message received!</h3>
              <p className="text-base font-medium text-gray-700 mb-6 max-w-sm">
                Thanks for reaching out. Our team will get back to you within 24 hours.
              </p>
              <button
                type="button"
                data-testid="contact-send-another"
                onClick={() => setSubmitted(false)}
                className="bg-[#FBD46D] border-[3px] border-gray-900 font-bold px-5 py-2.5 rounded-lg nb-shadow-sm nb-hover"
              >
                Send another
              </button>
            </div>
          ) : (
            <>
              {/* Honeypot field — hidden from humans, bots fill it */}
              <input
                type="checkbox"
                name="botcheck"
                checked={!!form.botcheck}
                onChange={(e) => setForm({ ...form, botcheck: e.target.checked ? "1" : "" })}
                tabIndex={-1}
                autoComplete="off"
                style={{ display: "none" }}
                aria-hidden="true"
              />

              <div className="grid sm:grid-cols-2 gap-4">
                <div>
                  <label className="block text-xs font-bold uppercase tracking-widest mb-2">Name *</label>
                  <input
                    data-testid="contact-input-name"
                    name="name"
                    value={form.name}
                    onChange={onChange}
                    className={inputCls}
                    placeholder="Jane Doe"
                    required
                  />
                </div>
                <div>
                  <label className="block text-xs font-bold uppercase tracking-widest mb-2">Email *</label>
                  <input
                    data-testid="contact-input-email"
                    type="email"
                    name="email"
                    value={form.email}
                    onChange={onChange}
                    className={inputCls}
                    placeholder="jane@company.com"
                    required
                  />
                </div>
              </div>

              <div className="grid sm:grid-cols-2 gap-4">
                <div>
                  <label className="block text-xs font-bold uppercase tracking-widest mb-2">Phone</label>
                  <input
                    data-testid="contact-input-phone"
                    name="phone"
                    value={form.phone}
                    onChange={onChange}
                    className={inputCls}
                    placeholder="+91 ..."
                  />
                </div>
                <div>
                  <label className="block text-xs font-bold uppercase tracking-widest mb-2">Company</label>
                  <input
                    data-testid="contact-input-company"
                    name="company"
                    value={form.company}
                    onChange={onChange}
                    className={inputCls}
                    placeholder="Your company"
                  />
                </div>
              </div>

              <div>
                <label className="block text-xs font-bold uppercase tracking-widest mb-2">Service of interest</label>
                <select
                  data-testid="contact-input-service"
                  name="service"
                  value={form.service}
                  onChange={onChange}
                  className={inputCls}
                >
                  <option value="">Select a service…</option>
                  {services.map((s) => (
                    <option key={s} value={s}>{s}</option>
                  ))}
                </select>
              </div>

              <div>
                <label className="block text-xs font-bold uppercase tracking-widest mb-2">Message *</label>
                <textarea
                  data-testid="contact-input-message"
                  name="message"
                  value={form.message}
                  onChange={onChange}
                  rows={5}
                  className={inputCls}
                  placeholder="Tell us about your project, timeline and goals..."
                  required
                />
              </div>

              <button
                type="submit"
                data-testid="contact-submit"
                disabled={submitting}
                className="w-full bg-[#FF477E] text-white border-[4px] border-gray-900 font-bold text-lg px-7 py-4 rounded-xl nb-shadow-sm nb-hover inline-flex items-center justify-center gap-2 disabled:opacity-70 disabled:cursor-not-allowed"
              >
                {submitting ? (
                  <>
                    <Loader2 size={20} className="animate-spin" /> Sending…
                  </>
                ) : (
                  <>
                    Send message <Send size={18} />
                  </>
                )}
              </button>
              <p className="text-xs font-medium text-gray-500 text-center">
                We reply within 24 hours. Your data stays with us — no third parties.
              </p>
            </>
          )}
        </form>
      </div>
    </section>
  );
};

export default Contact;
